import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, Users, Mail, Phone, Search, Building2, CreditCard, MessageSquare, Download, Eye, EyeOff, Copy, AlertTriangle, CheckCircle2, KeyRound, RotateCcw, ArrowUp, ArrowDown, ArrowUpDown, Table } from 'lucide-react';
import { propietariosApi, unidadesApi, edificiosApi } from '../../lib/api';
import { getStatusLabel } from '../../lib/utils';
import { StatusBadge } from '../../components/common/StatusBadge';
import { LoadingState, EmptyState, ErrorState } from '../../components/common/EmptyState';
import { BaseCompletaDialog } from '../../components/BaseCompletaDialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const initialFormData = {
  // ─── Titular ───
  tipo: 'persona_humana',
  nombre: '',
  apellido: '',
  razon_social: '',
  nombre_razon_social: '',  // legacy/derivado
  dni: '',
  email_legal: '',
  
  // Contacto
  email: '',
  telefono: '',
  whatsapp: '',
  direccion: '',
  localidad: '',
  provincia: '',
  codigo_postal: '',
  preferencia_contacto: 'email',
  
  // Datos Fiscales
  razon_social_fiscal: '',
  cuit_cuil: '',
  condicion_iva: '',
  domicilio_fiscal: '',
  tipo_factura: '',
  email_facturacion: '',
  
  // Datos Bancarios
  cbu: '',
  alias_cbu: '',
  banco: '',
  titular_cuenta: '',

  // Datos Comerciales (todos opcionales)
  cumpleanos: '',
  inversor_desde: '',
  perfil_breve: '',
  origen_vinculo: '',
  intereses: [],
  capacidad_inversion: '',
  notas_comerciales: '',

  // Otros
  notas: '',
  // Persona de contacto
  contact_is_same_as_owner: true,  // default: humana = misma persona
  contact_person_name: '',
  contact_person_first_name: '',
  contact_person_last_name: '',
  contact_person_role: '',
  contact_person_phone: '',
  contact_person_whatsapp: '',
  contact_person_email: '',
};

// Mapea respuestas de error del backend a mensajes claros para el usuario.
// FastAPI puede devolver:
//  - 400 con {detail: "string"} (errores de negocio: email duplicado, etc.)
//  - 422 con {detail: [{loc, msg, type}, ...]} (validación Pydantic)
//  - 401/403 con {detail: "..."} (sesión / permisos)
//  - 500 (error interno) o sin response (red caída)
function parsePropietarioError(err) {
  if (!err.response) {
    return 'Error de red. Verificá tu conexión e intentá de nuevo.';
  }
  const status = err.response.status;
  const detail = err.response.data?.detail;

  // 401: el interceptor ya redirige a /login; mensaje preventivo
  if (status === 401) return 'Sesión expirada. Volvé a iniciar sesión.';
  if (status === 403) return 'No tenés permisos para esta acción.';
  if (status === 404) return 'Propietario no encontrado. Refrescá la lista.';

  // 422: validación Pydantic (detail es array)
  if (status === 422 && Array.isArray(detail)) {
    const errs = detail.map(e => {
      const campo = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : (e.loc || 'campo');
      const msg = e.msg || 'inválido';
      return `${campo}: ${msg}`;
    });
    return errs.length === 1
      ? `Dato inválido — ${errs[0]}`
      : `Datos inválidos:\n- ${errs.join('\n- ')}`;
  }

  // 400 con detail string (regla de negocio)
  if (typeof detail === 'string' && detail.trim().length > 0) {
    // Mensajes específicos conocidos
    if (/email.*registrado|email.*ya.*existe|duplicad/i.test(detail)) {
      return 'Ese email ya está usado por otro usuario. Probá con otro.';
    }
    return detail;
  }

  // 500 / fallback
  if (status >= 500) return 'Error interno del servidor. Reintentá en unos segundos.';
  return 'Error al guardar. Revisá los datos e intentá de nuevo.';
}

// Helper para formatear fechas ISO a dd/MM/yyyy HH:mm. Devuelve "—" si vacío.
function _fmtFecha(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_e) { return '—'; }
}

// Bloque "Acceso del propietario" para el modal de edición.
// Muestra estado + fechas + botón Resetear contraseña.
// NUNCA muestra password, hash ni datos técnicos.
function AccesoBlock({ propietario, onResetClick }) {
  const ultimoIngreso = propietario?.last_login;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="acceso-block">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Acceso</span>
        </div>
        {ultimoIngreso ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-emerald-100 text-emerald-800 border-emerald-200"
            data-testid="acceso-estado-badge"
            title={`Último ingreso: ${_fmtFecha(ultimoIngreso)}`}
          >
            Último ingreso: {_fmtFecha(ultimoIngreso)}
          </span>
        ) : (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-slate-100 text-slate-700 border-slate-200"
            data-testid="acceso-estado-badge"
            title="El propietario nunca inició sesión"
          >
            Sin activar
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600">
        <div>
          <span className="text-slate-400">Último ingreso:</span> <span data-testid="acceso-last-login">{_fmtFecha(propietario?.last_login)}</span>
        </div>
        <div>
          <span className="text-slate-400">Último cambio:</span> <span data-testid="acceso-password-changed-at">{_fmtFecha(propietario?.password_changed_at)}</span>
        </div>
        <div className="col-span-2">
          <span className="text-slate-400">Último reset solicitado:</span> <span data-testid="acceso-reset-at">{_fmtFecha(propietario?.password_reset_requested_at)}</span>
          {propietario?.password_reset_by && (
            <span className="text-slate-400 ml-1">por {propietario.password_reset_by}</span>
          )}
        </div>
      </div>
      <div className="pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-orange-700 border-orange-200 hover:bg-orange-50"
          onClick={onResetClick}
          data-testid="btn-reset-password-prop"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          Resetear acceso
        </Button>
      </div>
    </div>
  );
}

// Bloque "Persona de Contacto" - tab Contacto del modal de Propietario.
// Para Persona Humana: pregunta si la persona de contacto es la misma que el titular.
// Para Persona Jurídica: muestra directamente los campos de persona de contacto (obligatorio).
function PersonaContactoBlock({ formData, setFormData }) {
  const tipo = formData.tipo || 'persona_humana';
  const isJuridica = tipo === 'persona_juridica';
  // Para jurídicas forzamos `contact_is_same_as_owner = false` y ocultamos la pregunta.
  const isSame = isJuridica ? false : !!formData.contact_is_same_as_owner;
  const showContactFields = isJuridica || !isSame;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="persona-contacto-block">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-700">Persona de contacto</span>
      </div>

      {!isJuridica && (
        <div className="space-y-1.5" data-testid="contact-same-question">
          <Label className="text-xs text-slate-600">¿Es la misma persona que el titular?</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={isSame ? 'default' : 'outline'}
              className="h-8"
              onClick={() => setFormData({
                ...formData,
                contact_is_same_as_owner: true,
                contact_person_name: '',
                contact_person_role: '',
                contact_person_phone: '',
                contact_person_whatsapp: '',
                contact_person_email: '',
              })}
              data-testid="contact-same-yes"
            >
              Sí, es la misma persona
            </Button>
            <Button
              type="button"
              size="sm"
              variant={!isSame ? 'default' : 'outline'}
              className="h-8"
              onClick={() => setFormData({ ...formData, contact_is_same_as_owner: false })}
              data-testid="contact-same-no"
            >
              No, es otra persona
            </Button>
          </div>
        </div>
      )}

      {showContactFields && (
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2 md:col-span-1">
              <Label htmlFor="contact_person_name" className="text-xs text-slate-600">
                Nombre y apellido {isJuridica ? <span className="text-red-500">*</span> : ''}
              </Label>
              <Input
                id="contact_person_name"
                value={formData.contact_person_name || ''}
                onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                placeholder="Juan Pérez"
                required={isJuridica}
                data-testid="contact-input-name"
              />
            </div>
            <div className="space-y-1.5 col-span-2 md:col-span-1">
              <Label htmlFor="contact_person_role" className="text-xs text-slate-600">
                {isJuridica ? 'Cargo o relación' : 'Relación con el titular'} (opcional)
              </Label>
              <Input
                id="contact_person_role"
                value={formData.contact_person_role || ''}
                onChange={(e) => setFormData({ ...formData, contact_person_role: e.target.value })}
                placeholder={isJuridica ? 'socio, gerente, apoderado, contador' : 'padre, madre, hijo, apoderado, contador'}
                data-testid="contact-input-role"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-3 md:col-span-1">
              <Label htmlFor="contact_person_phone" className="text-xs text-slate-600">Teléfono</Label>
              <Input
                id="contact_person_phone"
                value={formData.contact_person_phone || ''}
                onChange={(e) => setFormData({ ...formData, contact_person_phone: e.target.value })}
                placeholder="+54 261 ..."
                data-testid="contact-input-phone"
              />
            </div>
            <div className="space-y-1.5 col-span-3 md:col-span-1">
              <Label htmlFor="contact_person_whatsapp" className="text-xs text-slate-600">WhatsApp</Label>
              <Input
                id="contact_person_whatsapp"
                value={formData.contact_person_whatsapp || ''}
                onChange={(e) => setFormData({ ...formData, contact_person_whatsapp: e.target.value })}
                placeholder="+54 9 261 ..."
                data-testid="contact-input-whatsapp"
              />
            </div>
            <div className="space-y-1.5 col-span-3 md:col-span-1">
              <Label htmlFor="contact_person_email" className="text-xs text-slate-600">Email</Label>
              <Input
                id="contact_person_email"
                type="email"
                value={formData.contact_person_email || ''}
                onChange={(e) => setFormData({ ...formData, contact_person_email: e.target.value })}
                placeholder="contacto@..."
                data-testid="contact-input-email"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const AnalistaPropietarios = () => {
  const [propietarios, setPropietarios] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPropietario, setSelectedPropietario] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEdificio, setFilterEdificio] = useState('todos'); // 'todos' | 'torre_emilia' | 'foresta'
  const [filterEstadoInversor, setFilterEstadoInversor] = useState('todos'); // 'todos' | 'activos' | 'historicos'
  // Ordenamiento de la tabla. sortBy=null => orden por defecto (el del API).
  // Columnas soportadas: 'propietario' | 'tipo' | 'contacto' | 'emilia' | 'foresta' | 'estado' | 'usuario'.
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [activeTab, setActiveTab] = useState('basicos');
  // Estado para el flujo de "Resetear contraseña" del propietario seleccionado.
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);
  const [isBaseCompletaOpen, setIsBaseCompletaOpen] = useState(false);

  // Helpers null-safe -----------------------------------------------------
  const safeStr = (v) => String(v ?? '').toLowerCase();

  // Regla de la columna "Contacto":
  // - Persona Humana con contact_is_same_as_owner=true  → ''  (la celda muestra '—')
  // - Persona Humana con contact_is_same_as_owner=false → contact_person_name (o '' si vacío)
  // - Persona Jurídica                                  → contact_person_name (o '' si vacío)
  // NO mostrar teléfono, WhatsApp ni email. Solo el NOMBRE de la persona de contacto.
  const extractContacto = (p) => {
    const tipo = p.tipo || 'persona_humana';
    if (tipo !== 'persona_juridica' && p.contact_is_same_as_owner !== false) {
      // Humana con misma persona como contacto → no se muestra contacto separado
      return '';
    }
    return (p.contact_person_name || '').trim();
  };

  const tipoLabel = (tipo) => {
    if (tipo === 'persona_juridica') return 'Persona Jurídica';
    return 'Persona Humana'; // soporta persona_humana y persona_fisica legacy
  };

  // Mapa edificio_id -> nombre
  const edificioById = useMemo(() => {
    const map = {};
    for (const e of edificios) map[e.edificio_id] = e;
    return map;
  }, [edificios]);

  // Identifica edificios por nombre (case-insensitive, sin acentos)
  const findEdificioId = (target) => {
    const t = safeStr(target);
    const e = edificios.find((x) => safeStr(x.nombre).includes(t));
    return e?.edificio_id;
  };
  const idTorreEmilia = useMemo(() => findEdificioId('torre emilia'), [edificios]);
  const idForesta = useMemo(() => findEdificioId('foresta'), [edificios]);

  // Cuenta unidades PROPIETARIAS reales de un edificio por propietario_id.
  // Reglas:
  //   1. Excluye cocheras componentes (rol_unidad === 'componente'): heredan de su principal.
  //   2. Agrupa unidades que comparten conjunto_alquilable_id (ej. Piso 1, Cocheras Subsuelo):
  //      cuentan como 1 unidad propietaria por propietario, no como N.
  //   3. El resto (oficinas/locales principales, cocheras sueltas reales) cuenta 1:1.
  // Soporta `propietario_id` (singular) y `propietarios[]` (co-propietarios).
  const countUnidadesByPropietario = useMemo(() => {
    const map = {}; // key: `${propId}|${edifId}` -> count
    // Dedup por (propId, edifId, claveAgrupacion).
    // claveAgrupacion = conjunto_alquilable_id si existe, sino unidad_id (único).
    const seen = {}; // key: `${propId}|${edifId}|${claveAgrupacion}` -> true
    for (const u of unidades) {
      // Regla 1: las componentes no cuentan como propiedad independiente.
      if (u.rol_unidad === 'componente') continue;

      const owners = new Set();
      if (u.propietario_id) owners.add(u.propietario_id);
      if (Array.isArray(u.propietarios)) {
        for (const p of u.propietarios) {
          if (p?.propietario_id) owners.add(p.propietario_id);
          if (typeof p === 'string') owners.add(p);
        }
      }
      // Regla 2: agrupar por conjunto si existe; sino cada unidad es única.
      const claveAgrupacion = u.conjunto_alquilable_id || `__solo__${u.unidad_id}`;
      for (const pid of owners) {
        const dedupKey = `${pid}|${u.edificio_id}|${claveAgrupacion}`;
        if (seen[dedupKey]) continue;
        seen[dedupKey] = true;
        const k = `${pid}|${u.edificio_id}`;
        map[k] = (map[k] || 0) + 1;
      }
    }
    return map;
  }, [unidades]);

  const countUnidades = (propId, edifId) => {
    if (!propId || !edifId) return 0;
    return countUnidadesByPropietario[`${propId}|${edifId}`] || 0;
  };

  // Conjuntos de propietario_ids con al menos 1 unidad en cada edificio
  const propietariosByEdificio = useMemo(() => {
    const torre = new Set();
    const foresta = new Set();
    for (const k of Object.keys(countUnidadesByPropietario)) {
      const [pid, edifId] = k.split('|');
      if (edifId === idTorreEmilia) torre.add(pid);
      if (edifId === idForesta) foresta.add(pid);
    }
    return { torre, foresta };
  }, [countUnidadesByPropietario, idTorreEmilia, idForesta]);

  // Estado inversor automático: activo si tiene ≥1 unidad propietaria actual en TE o Foresta.
  const esInversorActivo = (pid) =>
    propietariosByEdificio.torre.has(pid) || propietariosByEdificio.foresta.has(pid);

  const countByFiltro = useMemo(() => ({
    todos: propietarios.length,
    activos: propietarios.filter(p => esInversorActivo(p.propietario_id)).length,
    historicos: propietarios.filter(p => !esInversorActivo(p.propietario_id)).length,
    torre_emilia: propietarios.filter(p => propietariosByEdificio.torre.has(p.propietario_id)).length,
    foresta: propietarios.filter(p => propietariosByEdificio.foresta.has(p.propietario_id)).length,
    ninguna: propietarios.filter(p =>
      !propietariosByEdificio.torre.has(p.propietario_id) &&
      !propietariosByEdificio.foresta.has(p.propietario_id)
    ).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [propietarios, propietariosByEdificio]);

  // Calcula si a un propietario le "falta info" usando la regla estricta
  // (matchea el backend `_info_completa`). Devuelve true si está incompleto.
  //
  // Humana mínimos: nombre+apellido, dni, email, whatsapp, direccion + fiscales (cuit_cuil, razon_social_fiscal, condicion_iva, domicilio_fiscal).
  // Jurídica mínimos: razon_social, dni, email, whatsapp, direccion + contacto (nombre + apellido) + fiscales.
  const computeFaltaInfo = (propietario) => {
    const p = propietario || {};
    const isJuridica = p.tipo === 'persona_juridica';
    const isHumana = p.tipo === 'persona_humana' || p.tipo === 'persona_fisica';
    if (!isHumana && !isJuridica) return true;

    // Comunes
    if (!p.dni || !p.email || !p.whatsapp || !p.direccion) return true;

    // Identidad según tipo
    if (isHumana) {
      if (!(p.nombre && p.apellido)) {
        const full = (p.nombre_razon_social || '').trim();
        if (!full || full.split(/\s+/).length < 2) return true;
      }
    } else {
      if (!(p.razon_social || p.nombre_razon_social)) return true;
      const cf = (p.contact_person_first_name || '').trim();
      const cl = (p.contact_person_last_name || '').trim();
      if (!(cf && cl)) {
        const full = (p.contact_person_name || '').trim();
        if (!full || full.split(/\s+/).length < 2) return true;
      }
    }

    // Fiscales obligatorios para ambos
    if (!p.cuit_cuil || !p.razon_social_fiscal || !p.condicion_iva || !p.domicilio_fiscal) return true;

    return false;
  };

  // Filter propietarios based on search term (null-safe), filtro de edificio y estado inversor
  const filteredPropietarios = useMemo(() => {
    let lst = propietarios;
    if (filterEdificio === 'torre_emilia') {
      lst = lst.filter(p => propietariosByEdificio.torre.has(p.propietario_id));
    } else if (filterEdificio === 'foresta') {
      lst = lst.filter(p => propietariosByEdificio.foresta.has(p.propietario_id));
    } else if (filterEdificio === 'ninguna') {
      // Propietarios sin unidades en TE ni en Foresta (incluye también propietarios
      // sin ninguna unidad, ej. cedentes, propietarios históricos, etc.).
      lst = lst.filter(p =>
        !propietariosByEdificio.torre.has(p.propietario_id) &&
        !propietariosByEdificio.foresta.has(p.propietario_id)
      );
    }
    if (filterEstadoInversor === 'activos') {
      lst = lst.filter(p => esInversorActivo(p.propietario_id));
    } else if (filterEstadoInversor === 'historicos') {
      lst = lst.filter(p => !esInversorActivo(p.propietario_id));
    }
    if (!searchTerm.trim()) return lst;
    const term = safeStr(searchTerm);
    return lst.filter((p) =>
      safeStr(p.nombre_razon_social).includes(term) ||
      safeStr(tipoLabel(p.tipo)).includes(term) ||
      safeStr(extractContacto(p)).includes(term) ||
      safeStr(p.cuit_cuil).includes(term) ||
      safeStr(p.email).includes(term)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propietarios, searchTerm, filterEdificio, filterEstadoInversor, propietariosByEdificio]);

  // Aplica ordenamiento sobre la lista YA filtrada (filtro + búsqueda primero).
  const sortedPropietarios = useMemo(() => {
    if (!sortBy) return filteredPropietarios;
    const dir = sortDir === 'desc' ? -1 : 1;
    const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

    // Devuelve la "clave" usada para comparar según la columna.
    // Para columnas booleanas devolvemos un número (0/1) para que la lógica
    // asc/desc se aplique igual que en columnas numéricas.
    const getKey = (p) => {
      switch (sortBy) {
        case 'propietario':
          return (p.nombre_razon_social || '').trim();
        case 'tipo':
          return tipoLabel(p.tipo);
        case 'contacto':
          return (extractContacto(p) || '').trim();
        case 'emilia':
          return countUnidades(p.propietario_id, idTorreEmilia) || 0;
        case 'foresta':
          return countUnidades(p.propietario_id, idForesta) || 0;
        case 'estado':
          // 0 = Falta info, 1 = Completo  => asc => "Falta info primero"
          return computeFaltaInfo(p) ? 0 : 1;
        case 'usuario':
          // 0 = Pendiente, 1 = Activo  => asc => "Pendiente primero"
          return p.activated_at ? 1 : 0;
        default:
          return '';
      }
    };

    const isString = sortBy === 'propietario' || sortBy === 'tipo' || sortBy === 'contacto';

    const arr = [...filteredPropietarios];
    arr.sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);

      // "Contacto": vacíos SIEMPRE al final, sin importar la dirección.
      if (sortBy === 'contacto') {
        const aEmpty = !ka;
        const bEmpty = !kb;
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
      }

      if (isString) {
        return collator.compare(String(ka), String(kb)) * dir;
      }
      // numérico (incluye 0/1 de columnas booleanas)
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filteredPropietarios, sortBy, sortDir, idTorreEmilia, idForesta, countUnidadesByPropietario]);

  // Cambia la columna/orden al hacer click en el header.
  // Primer click en una columna nueva => asc.
  // Segundo click en la misma => desc.
  // Tercer click en la misma => limpia el sort (orden original).
  const handleSort = (col) => {
    if (sortBy !== col) {
      setSortBy(col);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortBy(null);
    setSortDir('asc');
  };

  // Ícono indicador para el header de una columna ordenable.
  const renderSortIcon = (col) => {
    if (sortBy !== col) {
      return <ArrowUpDown className="w-3 h-3 text-slate-400 shrink-0" />;
    }
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-slate-700 shrink-0" />
      : <ArrowDown className="w-3 h-3 text-slate-700 shrink-0" />;
  };

  useEffect(() => {
    fetchPropietarios();
  }, []);

  const fetchPropietarios = async () => {
    try {
      setLoading(true);
      const [propRes, edifRes, unidRes] = await Promise.all([
        propietariosApi.list(),
        edificiosApi.list().catch(() => ({ data: [] })),
        unidadesApi.list().catch(() => ({ data: [] })),
      ]);
      setPropietarios(propRes.data || []);
      setEdificios(edifRes.data || []);
      setUnidades(unidRes.data || []);
    } catch (err) {
      console.error('Error fetching propietarios:', err);
      setError('Error al cargar los propietarios');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setSelectedPropietario(null);
    setFormData(initialFormData);
    setActiveTab('titular');
    setIsDialogOpen(true);
  };

  // Helper: separa "Juan Pérez García" en {nombre: "Juan", apellido: "Pérez García"}.
  // Split fallback: SOLO se ejecuta cuando los campos separados están vacíos.
  // Regla: último token = apellido, resto = nombre (consistente con el export Excel).
  // Ejemplos:
  //   "Mariela Veronica Lana"   → { nombre: "Mariela Veronica", apellido: "Lana" }
  //   "Ana Fernanda Lopez"      → { nombre: "Ana Fernanda",     apellido: "Lopez" }
  //   "Juan Pérez"              → { nombre: "Juan",             apellido: "Pérez" }
  //   "Madonna"                 → { nombre: "Madonna",          apellido: "" }
  const splitNombreApellido = (full) => {
    if (!full) return { nombre: '', apellido: '' };
    const parts = full.trim().split(/\s+/);
    if (parts.length === 1) return { nombre: parts[0], apellido: '' };
    return { nombre: parts.slice(0, -1).join(' '), apellido: parts[parts.length - 1] };
  };

  const handleOpenEdit = (propietario) => {
    setSelectedPropietario(propietario);
    const tipo = propietario.tipo || 'persona_humana';
    const esHumana = tipo === 'persona_humana' || tipo === 'persona_fisica';
    // Derivar nombre/apellido/razon_social desde nombre_razon_social si no vienen del backend.
    let nombre = propietario.nombre || '';
    let apellido = propietario.apellido || '';
    let razon_social = propietario.razon_social || '';
    if (esHumana && !nombre && !apellido && propietario.nombre_razon_social) {
      const sp = splitNombreApellido(propietario.nombre_razon_social);
      nombre = sp.nombre;
      apellido = sp.apellido;
    } else if (!esHumana && !razon_social && propietario.nombre_razon_social) {
      razon_social = propietario.nombre_razon_social;
    }
    // Contacto: derivar first/last name desde contact_person_name si no vienen.
    let cFirst = propietario.contact_person_first_name || '';
    let cLast = propietario.contact_person_last_name || '';
    if (!cFirst && !cLast && propietario.contact_person_name) {
      const sp = splitNombreApellido(propietario.contact_person_name);
      cFirst = sp.nombre;
      cLast = sp.apellido;
    }

    setFormData({
      tipo,
      nombre,
      apellido,
      razon_social,
      nombre_razon_social: propietario.nombre_razon_social || '',
      dni: propietario.dni || '',
      email_legal: propietario.email_legal || '',
      email: propietario.email || '',
      telefono: propietario.telefono || '',
      whatsapp: propietario.whatsapp || '',
      direccion: propietario.direccion || '',
      localidad: propietario.localidad || '',
      provincia: propietario.provincia || '',
      codigo_postal: propietario.codigo_postal || '',
      preferencia_contacto: propietario.preferencia_contacto || 'email',
      razon_social_fiscal: propietario.razon_social_fiscal || '',
      cuit_cuil: propietario.cuit_cuil || '',
      condicion_iva: propietario.condicion_iva || '',
      domicilio_fiscal: propietario.domicilio_fiscal || '',
      tipo_factura: propietario.tipo_factura || '',
      email_facturacion: propietario.email_facturacion || '',
      cbu: propietario.cbu || '',
      alias_cbu: propietario.alias_cbu || '',
      banco: propietario.banco || '',
      titular_cuenta: propietario.titular_cuenta || '',
      cumpleanos: propietario.cumpleanos || '',
      inversor_desde: propietario.inversor_desde || '',
      perfil_breve: propietario.perfil_breve || '',
      origen_vinculo: propietario.origen_vinculo || '',
      intereses: Array.isArray(propietario.intereses) ? propietario.intereses : [],
      capacidad_inversion: propietario.capacidad_inversion || '',
      notas_comerciales: propietario.notas_comerciales || '',
      notas: propietario.notas || '',
      contact_is_same_as_owner: typeof propietario.contact_is_same_as_owner === 'boolean'
        ? propietario.contact_is_same_as_owner
        : (propietario.tipo !== 'persona_juridica'),
      contact_person_name: propietario.contact_person_name || '',
      contact_person_first_name: cFirst,
      contact_person_last_name: cLast,
      contact_person_role: propietario.contact_person_role || '',
      contact_person_phone: propietario.contact_person_phone || '',
      contact_person_whatsapp: propietario.contact_person_whatsapp || '',
      contact_person_email: propietario.contact_person_email || '',
    });
    setActiveTab('titular');
    setIsDialogOpen(true);
  };

  const handleOpenDelete = (propietario) => {
    setSelectedPropietario(propietario);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const f = formData;
      const esHumana = f.tipo === 'persona_humana' || f.tipo === 'persona_fisica';
      const nrs = esHumana
        ? `${(f.nombre || '').trim()} ${(f.apellido || '').trim()}`.trim() || f.nombre_razon_social
        : (f.razon_social || '').trim() || f.nombre_razon_social;
      const submitData = {
        // Titular
        tipo: f.tipo,
        nombre_razon_social: nrs,
        nombre: esHumana ? (f.nombre || null) : null,
        apellido: esHumana ? (f.apellido || null) : null,
        razon_social: esHumana ? null : (f.razon_social || null),
        dni: f.dni || null,
        email_legal: f.email_legal || null,
        // Contacto
        email: f.email,
        telefono: f.telefono || null,
        whatsapp: f.whatsapp || null,
        direccion: f.direccion || null,
        localidad: f.localidad || null,
        provincia: f.provincia || null,
        codigo_postal: f.codigo_postal || null,
        preferencia_contacto: f.preferencia_contacto || null,
        // Fiscal
        razon_social_fiscal: f.razon_social_fiscal || null,
        cuit_cuil: f.cuit_cuil || null,
        condicion_iva: f.condicion_iva || null,
        domicilio_fiscal: f.domicilio_fiscal || null,
        tipo_factura: f.tipo_factura || null,
        email_facturacion: f.email_facturacion || null,
        // Bancario
        cbu: f.cbu || null,
        alias_cbu: f.alias_cbu || null,
        banco: f.banco || null,
        titular_cuenta: f.titular_cuenta || null,
        // Comerciales
        cumpleanos: f.cumpleanos || null,
        inversor_desde: f.inversor_desde || null,
        perfil_breve: f.perfil_breve || null,
        origen_vinculo: f.origen_vinculo || null,
        intereses: (f.intereses && f.intereses.length > 0) ? f.intereses : null,
        capacidad_inversion: f.capacidad_inversion || null,
        notas_comerciales: f.notas_comerciales || null,
        notas: f.notas || null,
        // Persona de contacto
        contact_is_same_as_owner: !!f.contact_is_same_as_owner,
        contact_person_name: f.contact_is_same_as_owner
          ? null
          : (f.contact_person_first_name || f.contact_person_last_name
              ? `${(f.contact_person_first_name || '').trim()} ${(f.contact_person_last_name || '').trim()}`.trim()
              : (f.contact_person_name || null)),
        contact_person_first_name: f.contact_is_same_as_owner ? null : (f.contact_person_first_name || null),
        contact_person_last_name:  f.contact_is_same_as_owner ? null : (f.contact_person_last_name  || null),
        contact_person_role: f.contact_person_role || null,
        contact_person_phone: f.contact_person_phone || null,
        contact_person_whatsapp: f.contact_person_whatsapp || null,
        contact_person_email: f.contact_person_email || null,
      };

      if (selectedPropietario) {
        await propietariosApi.update(selectedPropietario.propietario_id, submitData);
        toast.success('Propietario actualizado');
      } else {
        await propietariosApi.create(submitData);
        toast.success('Propietario creado. Envialé el link de activación para que cree su contraseña.');
      }
      setIsDialogOpen(false);
      fetchPropietarios();
    } catch (err) {
      console.error('Error saving propietario:', err);
      const msg = parsePropietarioError(err);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await propietariosApi.delete(selectedPropietario.propietario_id);
      toast.success('Propietario eliminado');
      setIsDeleteDialogOpen(false);
      fetchPropietarios();
    } catch (err) {
      console.error('Error deleting propietario:', err);
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Reset de contraseña: invalida la contraseña actual del propietario seleccionado.
  // NO borra propietario, ni usuario, ni unidades, ni datos fiscales, ni historial.
  const handleResetPassword = async () => {
    if (!selectedPropietario) return;
    setResettingPwd(true);
    try {
      await propietariosApi.resetPassword(selectedPropietario.propietario_id);
      toast.success('Contraseña invalidada. El propietario debe crear una nueva desde el link general.');
      setIsResetDialogOpen(false);
      setIsDialogOpen(false);
      fetchPropietarios();
    } catch (err) {
      console.error('Error resetting password:', err);
      toast.error(err.response?.data?.detail || 'No se pudo resetear la contraseña.');
    } finally {
      setResettingPwd(false);
    }
  };

  // Exportar a Excel (respeta búsqueda, filtro de edificio y estado inversor activos)
  const handleExportExcel = async () => {
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.append('q', searchTerm.trim());
    if (filterEdificio && filterEdificio !== 'todos') params.append('edificio', filterEdificio);
    if (filterEstadoInversor && filterEstadoInversor !== 'todos') params.append('inversor', filterEstadoInversor);
    const qs = params.toString();
    const url = `${propietariosApi.exportarExcel()}${qs ? `?${qs}` : ''}`;
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      // Intentar leer filename desde el header
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      const fileName = match
        ? match[1]
        : `propietarios_global_citizen_${new Date().toISOString().split('T')[0]}.xlsx`;

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      toast.success('Archivo Excel descargado');
    } catch (err) {
      console.error('Error exporting:', err);
      toast.error('Error al exportar el archivo: ' + (err.message || ''));
    }
  };

  if (loading) return <LoadingState message="Cargando propietarios..." />;
  if (error) return <ErrorState message={error} onRetry={fetchPropietarios} />;

  return (
    <div className="p-4 md:p-8 flex flex-col h-[calc(100vh-1rem)]">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Propietarios</h1>
          <p className="text-slate-500 mt-1 sr-only">Gestiona los propietarios de unidades</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Botón Copiar mensaje de activación (única acción de la fila superior) */}
          <Button
            variant="outline"
            onClick={() => {
              const baseUrl = window.location.origin;
              const link = `${baseUrl}/activar-propietario`;
              const mensaje = `Ingresá al link con tu email:

🔗 ${link}

📲 Para dejar la app instalada en el celular:

✅ Android:
Abrilo con Chrome, tocá los 3 puntitos y elegí "Agregar a pantalla principal" o "Instalar app".

✅ iPhone:
Abrilo con Safari, tocá Compartir y elegí "Agregar a pantalla de inicio".`;
              navigator.clipboard.writeText(mensaje).then(
                () => toast.success('Mensaje de activación copiado'),
                () => toast.error('No se pudo copiar')
              );
            }}
            data-testid="copy-activacion-msg-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copiar mensaje de activación
          </Button>
        </div>
      </div>

      {/* Search Bar + Filtros por Edificio + Acciones — todo en una sola fila */}
      {propietarios.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 flex-nowrap md:flex-nowrap flex-wrap">
            {/* Buscador */}
            <div className="relative flex-1 min-w-[140px] max-w-[240px] shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-propietarios"
              />
            </div>
            {/* Filtros edificio */}
            <div className="flex items-center gap-1 shrink-0" role="tablist" aria-label="Filtrar por edificio">
              {[
                { value: 'todos', label: 'Todos' },
                { value: 'torre_emilia', label: 'Torre Emilia' },
                { value: 'foresta', label: 'Foresta' },
                { value: 'ninguna', label: 'Ninguna' },
              ].map((opt) => (
                <Button
                  key={opt.value}
                  variant={filterEdificio === opt.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterEdificio(opt.value)}
                  data-testid={`filtro-edificio-${opt.value}`}
                  aria-pressed={filterEdificio === opt.value}
                  className="h-9"
                >
                  {opt.label}
                  <span className="ml-1.5 text-xs opacity-70">({countByFiltro[opt.value]})</span>
                </Button>
              ))}
            </div>
            {/* Botones a la derecha */}
            <div className="flex items-center gap-2 ml-auto shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBaseCompletaOpen(true)}
                disabled={propietarios.length === 0}
                data-testid="ver-base-completa-btn"
                className="h-9"
              >
                <Table className="w-4 h-4 mr-1" />
                Ver base
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                disabled={propietarios.length === 0}
                data-testid="export-excel-btn"
                className="h-9"
              >
                <Download className="w-4 h-4 mr-1" />
                Exportar Excel
              </Button>
              <Button
                size="sm"
                onClick={handleOpenCreate}
                data-testid="create-propietario-btn"
                className="h-9"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nuevo Propietario
              </Button>
            </div>
          </div>
          {/* Resumen visual oculto a pedido. Se mantiene en data-testid solo para tests/screen readers. */}
          {(searchTerm || filterEdificio !== 'todos') && (
            <p className="sr-only" data-testid="resumen-filtro-propietarios">
              {filteredPropietarios.length} propietarios filtrados
            </p>
          )}
        </div>
      )}

      {/* List */}
      {propietarios.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Sin propietarios"
          description="Aún no hay propietarios registrados."
          action={
            <Button onClick={handleOpenCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primer propietario
            </Button>
          }
        />
      ) : filteredPropietarios.length === 0 ? (
        <EmptyState
          icon={Search}
          title="Sin resultados"
          description={(() => {
            const edifLabels = { torre_emilia: 'Torre Emilia', foresta: 'Foresta', ninguna: 'sin unidades en TE ni Foresta' };
            const tag = edifLabels[filterEdificio];
            if (searchTerm) {
              return `No se encontraron propietarios con "${searchTerm}"${tag ? ` en ${tag}` : ''}`;
            }
            if (filterEdificio === 'ninguna') return 'No hay propietarios sin unidades en TE ni Foresta';
            return `No hay propietarios con unidades en ${tag || ''}`;
          })()}
          action={
            <Button variant="outline" onClick={() => { setSearchTerm(''); setFilterEdificio('todos'); setFilterEstadoInversor('todos'); }}>
              Limpiar filtros
            </Button>
          }
        />
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex-1 flex flex-col min-h-0">
          <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: '24%' }} />
                <col style={{ width: '10%' }} className="hidden md:table-column" />
                <col style={{ width: '14%' }} className="hidden lg:table-column" />
                <col style={{ width: '6%' }} className="hidden md:table-column" />
                <col style={{ width: '6%' }} className="hidden md:table-column" />
                <col style={{ width: '11%' }} className="hidden sm:table-column" />
                <col style={{ width: '7%' }} className="hidden sm:table-column" />
                <col style={{ width: '12%' }} className="hidden sm:table-column" />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead className="sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3 whitespace-nowrap bg-slate-50">
                    <button
                      type="button"
                      onClick={() => handleSort('propietario')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="sort-propietario"
                      aria-label="Ordenar por propietario"
                    >
                      Propietario
                      {renderSortIcon('propietario')}
                    </button>
                  </th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3 hidden md:table-cell whitespace-nowrap bg-slate-50">
                    <button
                      type="button"
                      onClick={() => handleSort('tipo')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="sort-tipo"
                      aria-label="Ordenar por tipo"
                    >
                      Tipo
                      {renderSortIcon('tipo')}
                    </button>
                  </th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3 hidden lg:table-cell whitespace-nowrap bg-slate-50">
                    <button
                      type="button"
                      onClick={() => handleSort('contacto')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="sort-contacto"
                      aria-label="Ordenar por contacto"
                    >
                      Contacto
                      {renderSortIcon('contacto')}
                    </button>
                  </th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-2 hidden md:table-cell whitespace-nowrap bg-slate-50">
                    <button
                      type="button"
                      onClick={() => handleSort('emilia')}
                      className="inline-flex items-center justify-center gap-1 w-full uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="sort-emilia"
                      aria-label="Ordenar por unidades en Torre Emilia"
                    >
                      Emilia
                      {renderSortIcon('emilia')}
                    </button>
                  </th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-2 hidden md:table-cell whitespace-nowrap bg-slate-50">
                    <button
                      type="button"
                      onClick={() => handleSort('foresta')}
                      className="inline-flex items-center justify-center gap-1 w-full uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="sort-foresta"
                      aria-label="Ordenar por unidades en Foresta"
                    >
                      Foresta
                      {renderSortIcon('foresta')}
                    </button>
                  </th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3 hidden sm:table-cell whitespace-nowrap bg-slate-50">
                    <button
                      type="button"
                      onClick={() => handleSort('estado')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="sort-estado"
                      aria-label="Ordenar por estado"
                    >
                      Estado
                      {renderSortIcon('estado')}
                    </button>
                  </th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-2 hidden sm:table-cell whitespace-nowrap bg-slate-50">
                    Email
                  </th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3 hidden sm:table-cell whitespace-nowrap bg-slate-50">
                    <button
                      type="button"
                      onClick={() => handleSort('usuario')}
                      className="inline-flex items-center gap-1 uppercase tracking-wider font-semibold text-slate-500 hover:text-slate-700"
                      data-testid="sort-usuario"
                      aria-label="Ordenar por estado de usuario"
                    >
                      Acceso
                      {renderSortIcon('usuario')}
                    </button>
                  </th>
                  <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-2 whitespace-nowrap bg-slate-50">
                    <span className="sr-only md:not-sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPropietarios.map((propietario) => {
                  const contacto = extractContacto(propietario);
                  const isJuridica = propietario.tipo === 'persona_juridica';
                  // Regla estricta de "Completo" — calculada por `computeFaltaInfo` (reutilizado).
                  const falta = computeFaltaInfo(propietario);
                  // ACCESO se basa en last_login (último ingreso real), no en estado_acceso.
                  const countTE = countUnidades(propietario.propietario_id, idTorreEmilia);
                  const countFor = countUnidades(propietario.propietario_id, idForesta);

                  return (
                    <tr
                      key={propietario.propietario_id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`propietario-row-${propietario.propietario_id}`}
                    >
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                            <Users className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {propietario.nombre_razon_social || '—'}
                            </p>
                            {propietario.email && (
                              <p className="text-xs text-slate-500 truncate">{propietario.email}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 hidden md:table-cell">
                        <span
                          className={
                            'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ' +
                            (isJuridica
                              ? 'bg-blue-50 text-blue-800 border-blue-200'
                              : 'bg-slate-50 text-slate-700 border-slate-200')
                          }
                          data-testid={`tipo-${propietario.propietario_id}`}
                        >
                          {tipoLabel(propietario.tipo)}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 hidden lg:table-cell">
                        <span
                          className="block text-sm text-slate-600 truncate"
                          title={contacto || ''}
                          data-testid={`contacto-${propietario.propietario_id}`}
                        >
                          {contacto || '—'}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center hidden md:table-cell">
                        <span
                          className={
                            'inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded text-sm font-medium ' +
                            (countTE > 0 ? 'bg-emerald-100 text-emerald-800' : 'text-slate-400')
                          }
                          data-testid={`count-emilia-${propietario.propietario_id}`}
                        >
                          {countTE}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center hidden md:table-cell">
                        <span
                          className={
                            'inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded text-sm font-medium ' +
                            (countFor > 0 ? 'bg-emerald-100 text-emerald-800' : 'text-slate-400')
                          }
                          data-testid={`count-foresta-${propietario.propietario_id}`}
                        >
                          {countFor}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 hidden sm:table-cell">
                        {falta ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
                            data-testid={`badge-falta-info-${propietario.propietario_id}`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            Falta info
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                            data-testid={`badge-completo-${propietario.propietario_id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Completo
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-center hidden sm:table-cell">
                        {(() => {
                          const tieneEmailAcceso = !!(propietario.email && String(propietario.email).trim());
                          return tieneEmailAcceso ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                              data-testid={`email-acceso-si-${propietario.propietario_id}`}
                              title={propietario.email}
                            >
                              Sí
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-800 border border-rose-200"
                              data-testid={`email-acceso-no-${propietario.propietario_id}`}
                              title="Falta email de acceso para activar la cuenta"
                            >
                              No
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2.5 px-3 hidden sm:table-cell">
                        {propietario.last_login ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                            data-testid={`acceso-ultimo-ingreso-${propietario.propietario_id}`}
                            title={`Último ingreso: ${_fmtFecha(propietario.last_login)}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            {_fmtFecha(propietario.last_login)}
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                            data-testid={`acceso-sin-activar-${propietario.propietario_id}`}
                            title="El propietario nunca inició sesión"
                          >
                            <KeyRound className="w-3 h-3" />
                            Sin activar
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-2">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-orange-600 hover:bg-orange-50"
                            onClick={() => { setSelectedPropietario(propietario); setIsResetDialogOpen(true); }}
                            title="Resetear acceso"
                            data-testid={`reset-acceso-row-${propietario.propietario_id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenEdit(propietario)}
                            data-testid={`edit-propietario-${propietario.propietario_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenDelete(propietario)}
                            data-testid={`delete-propietario-${propietario.propietario_id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPropietario ? 'Editar Propietario' : 'Nuevo Propietario'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-4">
                <TabsTrigger value="titular" data-testid="edit-tab-titular">Titular</TabsTrigger>
                <TabsTrigger value="contacto" data-testid="edit-tab-contacto">Contacto</TabsTrigger>
                <TabsTrigger value="fiscal" data-testid="edit-tab-fiscal">Fiscal</TabsTrigger>
                <TabsTrigger value="bancario" data-testid="edit-tab-bancario">Bancario</TabsTrigger>
                <TabsTrigger value="comercial" data-testid="tab-comercial">Comercial</TabsTrigger>
              </TabsList>

              {/* ─── Titular ─── */}
              <TabsContent value="titular" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tipo">Tipo de Persona *</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      tipo: value,
                      // Regla por defecto: Humana → Sí; Jurídica → No.
                      contact_is_same_as_owner: value !== 'persona_juridica',
                    })}
                  >
                    <SelectTrigger data-testid="select-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="persona_humana">Persona Humana</SelectItem>
                      <SelectItem value="persona_juridica">Persona Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(formData.tipo === 'persona_humana' || formData.tipo === 'persona_fisica') ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nombre">Nombre *</Label>
                      <Input
                        id="nombre"
                        value={formData.nombre || ''}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Juan"
                        required
                        data-testid="input-nombre"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apellido">Apellido *</Label>
                      <Input
                        id="apellido"
                        value={formData.apellido || ''}
                        onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                        placeholder="Pérez"
                        required
                        data-testid="input-apellido"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="razon_social">Razón Social *</Label>
                    <Input
                      id="razon_social"
                      value={formData.razon_social || ''}
                      onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                      placeholder="Empresa S.A."
                      required
                      data-testid="input-razon-social"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="dni">DNI / CUIT / CUIL</Label>
                  <Input
                    id="dni"
                    value={formData.dni || ''}
                    onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                    placeholder={(formData.tipo === 'persona_juridica') ? '30-12345678-9' : '12.345.678'}
                    data-testid="input-dni"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="direccion-legal">Dirección legal</Label>
                  <Input
                    id="direccion-legal"
                    value={formData.direccion || ''}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                    placeholder="Domicilio del titular"
                    data-testid="input-direccion-legal"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email-legal">Email legal</Label>
                  <Input
                    id="email-legal"
                    type="email"
                    value={formData.email_legal || ''}
                    onChange={(e) => {
                      const newLegal = e.target.value;
                      setFormData(f => {
                        // Si Contacto = Sí y el email de acceso está vacío o sigue al email_legal anterior,
                        // propagar el nuevo email legal a email de acceso para no duplicar la carga.
                        const sameContact = !!f.contact_is_same_as_owner && f.tipo !== 'persona_juridica';
                        const accessShouldFollow = sameContact && (!f.email || !f.email.trim() || f.email === f.email_legal);
                        return {
                          ...f,
                          email_legal: newLegal,
                          email: accessShouldFollow ? newLegal : f.email,
                        };
                      });
                    }}
                    placeholder="legal@ejemplo.com (opcional)"
                    data-testid="input-email-legal"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notas">Notas</Label>
                  <Textarea
                    id="notas"
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    placeholder="Notas adicionales sobre el propietario"
                    rows={3}
                    data-testid="input-notas"
                  />
                </div>
              </TabsContent>

              {/* ─── Contacto ─── */}
              <TabsContent value="contacto" className="space-y-4">
                <div className="space-y-2">
                  <Label>¿La persona de contacto es la misma que el titular?</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.contact_is_same_as_owner ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFormData(f => ({
                        ...f,
                        contact_is_same_as_owner: true,
                        // Si todavía no hay email de acceso, autocompletar desde el email legal del titular
                        // para no obligar a recargar el dato.
                        email: (f.email && f.email.trim()) ? f.email : (f.email_legal || ''),
                        // Reflejar nombre/apellido contacto = titular (aunque los inputs estén ocultos en este modo).
                        contact_person_first_name: f.nombre || '',
                        contact_person_last_name: f.apellido || '',
                      }))}
                      data-testid="contacto-mismo-si"
                      disabled={formData.tipo === 'persona_juridica'}
                    >Sí</Button>
                    <Button
                      type="button"
                      variant={!formData.contact_is_same_as_owner ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFormData({ ...formData, contact_is_same_as_owner: false })}
                      data-testid="contacto-mismo-no"
                    >No</Button>
                  </div>
                  {formData.tipo === 'persona_juridica' && (
                    <p className="text-xs text-slate-500">Para persona jurídica siempre debe definirse una persona de contacto.</p>
                  )}
                </div>

                {!formData.contact_is_same_as_owner && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre contacto *</Label>
                      <Input
                        value={formData.contact_person_first_name || ''}
                        onChange={(e) => setFormData({ ...formData, contact_person_first_name: e.target.value })}
                        placeholder="Juan"
                        required={!formData.contact_is_same_as_owner}
                        data-testid="contact-input-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Apellido contacto *</Label>
                      <Input
                        value={formData.contact_person_last_name || ''}
                        onChange={(e) => setFormData({ ...formData, contact_person_last_name: e.target.value })}
                        placeholder="Pérez"
                        required={!formData.contact_is_same_as_owner}
                        data-testid="contact-input-last-name"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email-acceso">Usuario / email de acceso *</Label>
                  <Input
                    id="email-acceso"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="usuario@ejemplo.com"
                    required
                    data-testid="input-email"
                  />
                  <p className="text-xs text-slate-500">Este email se usa también para que el propietario acceda al portal.</p>
                </div>

                {!selectedPropietario ? (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 space-y-1" data-testid="info-activacion-nuevo">
                    <p className="font-medium flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Activación de la cuenta</p>
                    <p>
                      Al guardar, el propietario queda en estado <strong>pendiente de activación</strong>.
                      Compartile el link general para que cree su contraseña:
                    </p>
                    <p className="font-mono text-[11px] break-all">
                      {window.location.origin}/activar-propietario
                    </p>
                  </div>
                ) : (
                  <AccesoBlock
                    propietario={selectedPropietario}
                    onResetClick={() => setIsResetDialogOpen(true)}
                  />
                )}

                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp</Label>
                  <Input
                    id="whatsapp"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    placeholder="+54 9 261 123 4567"
                    data-testid="input-whatsapp"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferencia">Preferencia de contacto</Label>
                  <Select
                    value={formData.preferencia_contacto}
                    onValueChange={(v) => setFormData({ ...formData, preferencia_contacto: v })}
                  >
                    <SelectTrigger id="preferencia"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              {/* Tab: Datos Fiscales */}
              <TabsContent value="fiscal" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="razon-social-fiscal">Nombre / Razón Social fiscal</Label>
                  <Input
                    id="razon-social-fiscal"
                    value={formData.razon_social_fiscal || ''}
                    onChange={(e) => setFormData({ ...formData, razon_social_fiscal: e.target.value })}
                    placeholder="Nombre que figura en facturación (puede diferir del titular)"
                    data-testid="input-razon-social-fiscal"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cuit_cuil">CUIT/CUIL</Label>
                    <Input
                      id="cuit_cuil"
                      value={formData.cuit_cuil}
                      onChange={(e) => {
                        // Auto-formatear CUIT: XX-XXXXXXXX-X
                        let value = e.target.value.replace(/\D/g, ''); // Solo números
                        if (value.length > 11) value = value.slice(0, 11);
                        
                        let formatted = '';
                        if (value.length > 0) {
                          formatted = value.slice(0, 2);
                        }
                        if (value.length > 2) {
                          formatted += '-' + value.slice(2, 10);
                        }
                        if (value.length > 10) {
                          formatted += '-' + value.slice(10, 11);
                        }
                        
                        setFormData({ ...formData, cuit_cuil: formatted });
                      }}
                      placeholder="20-12345678-9"
                      maxLength={13}
                      data-testid="input-cuit"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="condicion_iva">Condición IVA</Label>
                    <Select
                      value={formData.condicion_iva}
                      onValueChange={(value) => setFormData({ ...formData, condicion_iva: value })}
                    >
                      <SelectTrigger data-testid="select-condicion-iva">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                        <SelectItem value="monotributista">Monotributista</SelectItem>
                        <SelectItem value="exento">Exento</SelectItem>
                        <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="domicilio_fiscal">Domicilio Fiscal</Label>
                  <Input
                    id="domicilio_fiscal"
                    value={formData.domicilio_fiscal}
                    onChange={(e) => setFormData({ ...formData, domicilio_fiscal: e.target.value })}
                    placeholder="Domicilio según AFIP"
                    data-testid="input-domicilio-fiscal"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_facturacion">Email de facturación</Label>
                  <Input
                    id="email_facturacion"
                    type="email"
                    value={formData.email_facturacion}
                    onChange={(e) => setFormData({ ...formData, email_facturacion: e.target.value })}
                    placeholder="facturas@empresa.com"
                    data-testid="input-email-facturacion"
                  />
                  <p className="text-[11px] text-slate-400">Dirección donde se envían las facturas. Si está vacío, se usa el email de acceso.</p>
                </div>
              </TabsContent>

              {/* Tab: Datos Bancarios */}
              <TabsContent value="bancario" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cbu">CBU</Label>
                  <Input
                    id="cbu"
                    value={formData.cbu}
                    onChange={(e) => setFormData({ ...formData, cbu: e.target.value })}
                    placeholder="0000000000000000000000"
                    maxLength={22}
                    data-testid="input-cbu"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="alias_cbu">Alias</Label>
                  <Input
                    id="alias_cbu"
                    value={formData.alias_cbu}
                    onChange={(e) => setFormData({ ...formData, alias_cbu: e.target.value })}
                    placeholder="mi.alias"
                    data-testid="input-alias"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="banco">Banco</Label>
                  <Input
                    id="banco"
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                    placeholder="Banco Nación, Santander, etc."
                    data-testid="input-banco"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="titular_cuenta">Titular de la Cuenta</Label>
                  <Input
                    id="titular_cuenta"
                    value={formData.titular_cuenta}
                    onChange={(e) => setFormData({ ...formData, titular_cuenta: e.target.value })}
                    placeholder="Nombre del titular"
                    data-testid="input-titular"
                  />
                </div>
              </TabsContent>

              {/* Tab: Datos Comerciales */}
              <TabsContent value="comercial" className="space-y-4" data-testid="tab-comercial-content">
                <p className="text-xs text-slate-500 -mt-1">
                  Información comercial simple para entender rápido quién es el propietario y su potencial. Todos los campos son opcionales.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cumpleanos">Cumpleaños</Label>
                    <Input
                      id="cumpleanos"
                      type="date"
                      value={formData.cumpleanos}
                      onChange={(e) => setFormData({ ...formData, cumpleanos: e.target.value })}
                      data-testid="input-cumpleanos"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inversor_desde">Inversor desde</Label>
                    <Input
                      id="inversor_desde"
                      type="date"
                      value={formData.inversor_desde}
                      onChange={(e) => setFormData({ ...formData, inversor_desde: e.target.value })}
                      data-testid="input-inversor-desde"
                    />
                    <p className="text-[11px] text-slate-400">Podés usar el inicio del año si solo recordás el año (ej. 2018-01-01).</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="perfil_breve">Perfil breve</Label>
                  <textarea
                    id="perfil_breve"
                    value={formData.perfil_breve}
                    onChange={(e) => setFormData({ ...formData, perfil_breve: e.target.value })}
                    rows={2}
                    placeholder="Quién es, a qué se dedica, dato relevante."
                    className="w-full min-h-[60px] px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    data-testid="input-perfil-breve"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="origen_vinculo">Origen / tipo de vínculo</Label>
                    <Select
                      value={formData.origen_vinculo || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, origen_vinculo: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="select-origen-vinculo">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin especificar</SelectItem>
                        <SelectItem value="compro_a_gc">Compró a Global Citizen</SelectItem>
                        <SelectItem value="reventa">Reventa</SelectItem>
                        <SelectItem value="proveedor">Proveedor</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="capacidad_inversion">Capacidad de inversión</Label>
                    <Select
                      value={formData.capacidad_inversion || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, capacidad_inversion: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="select-capacidad-inversion">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin especificar</SelectItem>
                        <SelectItem value="baja">Baja</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Intereses</Label>
                  <div className="flex flex-wrap gap-4 pt-1">
                    {[
                      { value: 'renta', label: 'Renta' },
                      { value: 'plusvalia', label: 'Plusvalía' },
                      { value: 'financiamiento', label: 'Financiamiento' },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 cursor-pointer select-none"
                        data-testid={`label-interes-${opt.value}`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.intereses.includes(opt.value)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...formData.intereses, opt.value]
                              : formData.intereses.filter((i) => i !== opt.value);
                            setFormData({ ...formData, intereses: next });
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          data-testid={`checkbox-interes-${opt.value}`}
                        />
                        <span className="text-sm text-slate-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notas_comerciales">Notas comerciales</Label>
                  <textarea
                    id="notas_comerciales"
                    value={formData.notas_comerciales}
                    onChange={(e) => setFormData({ ...formData, notas_comerciales: e.target.value })}
                    rows={3}
                    placeholder="Cualquier dato comercial libre (preferencias, contexto, oportunidades, etc.)"
                    className="w-full min-h-[80px] px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    data-testid="input-notas-comerciales"
                  />
                </div>
              </TabsContent>

            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="submit-propietario">
                {saving ? 'Guardando...' : selectedPropietario ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar propietario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará al propietario "{selectedPropietario?.nombre_razon_social}" 
              y su cuenta de acceso. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation */}
      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent data-testid="reset-password-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Resetear acceso de "{selectedPropietario?.nombre_razon_social}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Esto invalidará la contraseña actual y permitirá que el propietario cree una
              nueva usando el link general. No se borrarán sus datos ni sus unidades.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-password-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetPassword}
              disabled={resettingPwd}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="reset-password-confirm-btn"
            >
              {resettingPwd ? 'Reseteando...' : 'Resetear acceso'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BaseCompletaDialog
        open={isBaseCompletaOpen}
        onClose={() => setIsBaseCompletaOpen(false)}
        title="Base completa de Propietarios"
        endpoint="/propietarios/preview-base"
        queryParams={{
          ...(searchTerm.trim() ? { q: searchTerm.trim() } : {}),
          ...(filterEdificio && filterEdificio !== 'todos' ? { edificio: filterEdificio } : {}),
          ...(filterEstadoInversor && filterEstadoInversor !== 'todos' ? { inversor: filterEstadoInversor } : {}),
        }}
        firstColumnIndex={1}
        rowIdKey="propietario_id"
      />
    </div>
  );
};

export default AnalistaPropietarios;
